import { Reducer } from 'redux'
import { CloudProvidersType } from '../../../admin/modules/NewCustomers/reducer'
import { CosellMappingManagerActionTypes } from './coSellMappingManagerActionTypes'

export enum CoSellMappingManagerSectionName {
  COMPANY_AND_CONTACT = 'companyAndContact',
  HUBSPOT_DEAL_PIPELINE = 'hubspotDealPipeline',
  AWS_PARTNER_USER_DETAILS = 'awsPartnerUserDetails',
  UPDATE_REFERRAL_RECORD = 'updateReferralRecord',
  SYNC_REFERRAL_RECORD = 'syncReferralRecord',
  LEAD_AND_REFERRAL_SYNC = 'leadAndReferralSync',
  AUTO_CREATE_ACCOUNT_AND_OPPORTUNITY = 'autoCreateAccountAndOpportunity',
  UPDATE_REFERRAL_RECORD_SALESFORCE = 'updateReferralRecordSalesforce',
}

export interface CoSellCompanyAndContact {
  createCompany: boolean
  createContact: boolean
  createSenderCompany: boolean
  awsSalesRep: boolean
  awsAccountManager: boolean
  awsIsvSuccessManager: boolean
  awsPartnerSuccessManager: boolean
  partnerDevelopmentManager: boolean
  wwpsPartnerDevelopmentManager: boolean
  customerEndUserContactDetails: boolean
  senderContact: boolean
}

export interface ReferralPipeline {
  pipelineId: string
  pipelineDealStageId: string
}

export interface CoSellHubspotDealPipeline {
  autoAcceptIncomingReferral: boolean
  opportunityReferralPipeline: ReferralPipeline
  autoAcceptIncomingLead: boolean
  leadReferralPipeline: ReferralPipeline
}

export interface AwsPartnerUserDetails {
  id: string
  email: string
  lastName: string
  firstName: string
}

export interface CoSellAwsPartnerUserDetails {
  userDetails: AwsPartnerUserDetails[]
}

export interface StagesMapping {
  prospect: string[]
  qualified: string[]
  technicalValidation: string[]
  businessValidation: string[]
  committed: string[]
  launched: string[]
  closedLost: string[]
}

export interface CoSellUpdateReferralRecord {
  stage: boolean
  dealOwner: boolean
  nextSteps: boolean
  targetCloseDate: boolean
  stagesMapping: StagesMapping
  stageFieldInternalName?: string
  committedTemplateId?: string
  launchedTemplateId?: string
  closedLostTemplateId?: string
}

export interface CoSellSyncReferralRecord {
  /** Toggle for syncing custom referral fields to CRM; persisted as `enable_referral_sync_to_crm` (outside sync_configuration). */
  syncCustomHubspotFieldsForReporting?: boolean
  enableReferralSyncToCrm?: boolean
}
export interface CoSellLeadAndReferralSync {
  labraReferralSync: boolean
  partnerOriginatedReferralSync: boolean
  awsOriginatedReferralSync: boolean
  labraLeadSync: boolean
  autoAcceptIncomingReferral: boolean
}
export interface CoSellAutoCreateAccountAndOpportunity {
  autoCreateAccount: boolean
  autoCreateOpportunity: boolean
  syncReferralDataToOpportunity: boolean
  autoCreateMappings?: Record<string, string>
}

export interface CoSellUpdateReferralRecordSalesforce {
  stage: boolean
  targetCloseDate: boolean
  stagesMapping: StagesMapping
  stageFieldInternalName?: string
  committedTemplateId?: string
  launchedTemplateId?: string
  closedLostTemplateId?: string
}
export type CosellMappingManagerSections = {
  [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: CoSellCompanyAndContact
  [CoSellMappingManagerSectionName.HUBSPOT_DEAL_PIPELINE]: CoSellHubspotDealPipeline
  [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: CoSellAwsPartnerUserDetails
  [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD]: CoSellUpdateReferralRecord
  [CoSellMappingManagerSectionName.SYNC_REFERRAL_RECORD]: CoSellSyncReferralRecord
}

export type CosellMappingManagerSectionsForSalesforce = {
  [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: CoSellCompanyAndContact
  [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: CoSellAwsPartnerUserDetails
  [CoSellMappingManagerSectionName.LEAD_AND_REFERRAL_SYNC]: CoSellLeadAndReferralSync
  [CoSellMappingManagerSectionName.AUTO_CREATE_ACCOUNT_AND_OPPORTUNITY]: CoSellAutoCreateAccountAndOpportunity
  [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD_SALESFORCE]: CoSellUpdateReferralRecordSalesforce
}

export enum MappingManagerTemplateStatusType {
  'DRAFT' = 'DRAFT',
  'ACTIVE' = 'ACTIVE',
}

export interface AutoCreateTriggerCriteria {
  object: string
  field: string
  value: string
}

export interface CosellTemplatesDetailsData {
  templateType?: string
  templateName: string
  templateId: string
  status:
    | MappingManagerTemplateStatusType.DRAFT
    | MappingManagerTemplateStatusType.ACTIVE
  createdBy: string
  creationDate: string
  lastUpdatedAt: string
  autoReferralCreation?: boolean
  autoReferralTriggerCriteria?: AutoCreateTriggerCriteria | null
  cloudProvider?: string
}

export interface CosellFieldMappingPicklistData {
  referralValue: string
  hubspotValue: string
  hubspotLabel: string
}

export interface CosellFieldMapping {
  referralField: string
  referralFieldType: string
  hubspotObject: string
  hubspotField: string
  hubspotFieldLabel: string
  hubspotFieldType: string
  isHidden: boolean
  isReadonly: boolean
  isMapped: boolean
  label: string
  required: boolean
  displayOrder: number
  defaultValue: string | string[]
  category: string
  picklist: CosellHubspotFieldsOptions[] | null
  picklistMappings: CosellFieldMappingPicklistData[] | null
  crmField?: string
  crmFieldLabel?: string
  crmFieldType?: string
}

export interface CosellTemplatesDataFieldMappings {
  companyName?: CosellFieldMapping
  website?: CosellFieldMapping
  country?: CosellFieldMapping
  state?: CosellFieldMapping
  city?: CosellFieldMapping
  address?: CosellFieldMapping
  postalCode?: CosellFieldMapping
  dataUniversalNumberSystem?: CosellFieldMapping
  industry?: CosellFieldMapping
  otherIndustry?: CosellFieldMapping
  classifiedNationalSecurityInformation?: CosellFieldMapping
  customerFirstName?: CosellFieldMapping
  customerLastName?: CosellFieldMapping
  customerEmail?: CosellFieldMapping
  customerTitle?: CosellFieldMapping
  customerPhone?: CosellFieldMapping
  partnerProjectTitle?: CosellFieldMapping
  targetCloseDate?: CosellFieldMapping
  estimatedAwsMonthlyRecurringRevenue?: CosellFieldMapping
  description?: CosellFieldMapping
  deliveryModel?: CosellFieldMapping
  useCase?: CosellFieldMapping
  opportunityType?: CosellFieldMapping
  opportunityOwnerEmail?: CosellFieldMapping
  partnerPrimaryNeedFromAws?: CosellFieldMapping
  salesActivities?: CosellFieldMapping
  isFromMarketingActivity?: CosellFieldMapping
  wasMdfUsed?: CosellFieldMapping
  marketingActivityChannel?: CosellFieldMapping
  marketingActivityUseCase?: CosellFieldMapping
  parentOpportunityId?: CosellFieldMapping
  nextSteps?: CosellFieldMapping
  solutionOffered?: CosellFieldMapping
  otherSolutionOffered?: CosellFieldMapping
  marketingCampaignCode?: CosellFieldMapping
  additionalComments?: CosellFieldMapping
  contractEffectiveDate?: CosellFieldMapping
  contractExpirationDate?: CosellFieldMapping
  currency?: CosellFieldMapping
  customerSoftwareValueTvc?: CosellFieldMapping
  procurementType?: CosellFieldMapping
  apnPrograms?: CosellFieldMapping
  awsProducts?: CosellFieldMapping
  customerAwsAccountId?: CosellFieldMapping
  competitiveTracking?: CosellFieldMapping
  competitiveTrackingOther?: CosellFieldMapping
  offerId?: CosellFieldMapping
  partnerSalesContactEmail?: CosellFieldMapping
  partnerSalesContactFirstName?: CosellFieldMapping
  partnerSalesContactLastName?: CosellFieldMapping
  partnerSalesContactPrimaryPhone?: CosellFieldMapping
  partnerContactTitle?: CosellFieldMapping
}

export interface CosellSyncNewReferralMapping {
  label: string
  category: string
  referralFieldType: string
  referralField: string
  crmObject: string
  crmField: string
  crmFieldType: string
  crmFieldLabel: string
  displayOrder: number
  syncToCrm: boolean
}

export interface CosellSyncReferralMapping {
  label: string
  category: string
  referralFieldType: string
  referralField: string
  hubspotField: string
  hubspotFieldType: string
  hubspotObject: string
  hubspotFieldLabel: string
  displayOrder: number
  syncToHubspot: boolean
}

/** One referral field mapping row — CRM shape when multi-cloud mapping is on, else HubSpot shape. */
export type CosellSyncReferralMappingEntry =
  | CosellSyncReferralMapping
  | CosellSyncNewReferralMapping

/** Partial merge for a single mapping row (keys from either HubSpot or CRM shape). */
export type CosellSyncReferralMappingPatch = Partial<
  CosellSyncReferralMapping & CosellSyncNewReferralMapping
>

export interface CosellReferralDataFieldMappings {
  companyName?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  website?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  country?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  state?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  city?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  address?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  postalCode?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  dataUniversalNumberSystem?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  industry?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  otherIndustry?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  classifiedNationalSecurityInformation?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  customerFirstName?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  customerLastName?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  customerEmail?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  customerTitle?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  customerPhone?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  partnerProjectTitle?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  targetCloseDate?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  estimatedAwsMonthlyRecurringRevenue?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  description?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  deliveryModel?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  useCase?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  opportunityType?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  parentOpportunityId?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  opportunityOwnerEmail?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  partnerPrimaryNeedFromAws?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  salesActivities?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  isFromMarketingActivity?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  wasMdfUsed?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  marketingActivityChannel?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  marketingActivityUseCase?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  marketingCampaignCode?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  nextSteps?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  solutionOffered?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  otherSolutionOffered?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  referralStage?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  referredBy?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  additionalComments?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  contractEffectiveDate?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  contractExpirationDate?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  currency?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  customerSoftwareValueTvc?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  procurementType?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  apnPrograms?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsProducts?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  customerAwsAccountId?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  competitiveTracking?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  competitiveTrackingOther?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  offerId?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsMarketplaceEngagementScore?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  awsInternalStage?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsCloseDate?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsClosedLostReason?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsRecommendedAction?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  customerSegment?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  isGreenfield?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsOpportunityId?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  partnerAcceptanceStatus?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  awsAcceptanceStatus?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsSalesRepEmail?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsSalesRepName?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsAccountOwnerName?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
  awsAccountOwnerEmail?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  awsIsvSuccessManagerName?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  awsIsvSuccessManagerEmail?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  awsPartnerSuccessManagerName?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  awsPartnerSuccessManagerEmail?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  partnerDevelopmentManagerName?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  partnerDevelopmentManagerEmail?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  otherPartnerDevelopmentManagerName?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  otherPartnerDevelopmentManagerEmail?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  partnerSalesContactEmail?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  partnerSalesContactFirstName?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  partnerSalesContactLastName?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  partnerSalesContactPrimaryPhone?:
    | CosellSyncReferralMapping
    | CosellSyncNewReferralMapping
  partnerContactTitle?: CosellSyncReferralMapping | CosellSyncNewReferralMapping
}

export interface CosellTemplateDataResponse {
  resetToDefault?: boolean
  id: string
  name: string
  templateType: string
  autoReferralCreation: boolean
  autoReferralDealtypeTrigger?: string
  cloudProvider: CloudProvidersType
  isDraft: boolean
  isActive?: boolean
  fieldMappings: CosellTemplatesDataFieldMappings
  createdAt: string
  updatedAt: string
  createdBy: string
  updatedBy: string
  crmId?: string
  accountId?: string
  productType?: string
  crmType?: string
  autoReferralTriggerCriteria?: AutoCreateTriggerCriteria | null
}

export interface CosellTemplateItem {
  id: string
  name: string
  crmId: string
  accountId: string
  templateType: string
  productType: string
  cloudProvider: string
  crmType: string
  isDraft: boolean
  autoReferralCreation: boolean
  createdBy: string
  createdAt: string
  updatedAt: string | null
  autoReferralTriggerCriteria?: AutoCreateTriggerCriteria | null
}

export interface MarketplaceSalesforceTemplateItem {
  id: string
  name: string
  crmId: string
  accountId: string
  templateType: string
  productType: string
  cloudProvider: string
  crmType: string
  isDraft: boolean
  autoReferralCreation: boolean
  createdBy: string
  createdAt: string
  updatedAt: string | null
  autoReferralTriggerCriteria?: AutoCreateTriggerCriteria | null
}

export interface MarketplaceSalesforceTemplateItem {
  id: string
  name: string
  crmId: string
  accountId: string
  templateType: string
  productType: string
  cloudProvider: string
  crmType: string
  isDraft: boolean
  autoReferralCreation: boolean
  createdBy: string
  createdAt: string
  updatedAt: string | null
}

export interface CosellTemplateDataResponseCRM {
  mappingTemplates: CosellTemplateItem[]
  totalCount: number
  pageNumber: number
  pageSize: number
  sortBy: string
  sortOrder: 'ASC' | 'DESC'
}

export interface MarketplaceTemplateDataResponseSalesforce {
  mappingTemplates: MarketplaceSalesforceTemplateItem[]
  totalCount: number
  pageNumber: number
  pageSize: number
  sortBy: string
  sortOrder: 'ASC' | 'DESC'
}

export interface CosellTemplateAutomateReferralCreation {
  autoReferralCreation: boolean
  autoReferralDealtypeTrigger?: string
  autoReferralTriggerCriteria?: Record<string, string> | null
}

export interface CosellTemplateSectionData {
  customerDetails: Partial<CosellTemplatesDataFieldMappings>
  referralDetails: Partial<CosellTemplatesDataFieldMappings>
  awsDetails: Partial<CosellTemplatesDataFieldMappings>
  contacts: Partial<CosellTemplatesDataFieldMappings>
  dealDetails: Partial<CosellTemplatesDataFieldMappings>
  solutionsOffered: Partial<CosellTemplatesDataFieldMappings>
  helpFromMicrosoft: Partial<CosellTemplatesDataFieldMappings>
  automateReferralCreation?: {
    autoReferralCreation: boolean
    autoReferralTriggerCriteria: AutoCreateTriggerCriteria | null
  }
}

export interface CosellReferralMappingSectionData {
  customerDetails: Partial<CosellReferralDataFieldMappings>
  referralDetails: Partial<CosellReferralDataFieldMappings>
  awsDetails: Partial<CosellReferralDataFieldMappings>
  contacts: Partial<CosellReferralDataFieldMappings>
}

export interface CosellTemplateData {
  templateData: CosellTemplateDataResponse
  sectionData: CosellTemplateSectionData
}

export interface CosellHubspotFieldsOptions {
  value: string
  label: string
  options?: CosellHubspotFieldsOptions[]
}

export interface CosellHubspotFieldsData {
  internalName: string
  displayLabel: string
  fieldType: string
  objectType: string
  description: string
  options?: CosellHubspotFieldsOptions[]
}
export interface StandardValue {
  label: string
  value: string
}
export interface FieldDefinition {
  label: string
  name: string
  type: string
  isPrimaryKey: boolean
  isMandatory: boolean
  mappedDataType: string
  maxLength: number
  standardValues: StandardValue[] | null
}
export interface CosellSalesforceFieldsData {
  Opportunity: FieldDefinition[]
  Account: FieldDefinition[]
}

export interface CosellHubspotFieldsDataResponse {
  fields: CosellHubspotFieldsData[]
}

export interface CosellTemplateReferralMapping {
  referralMappings: CosellReferralDataFieldMappings
  sectionData: CosellReferralMappingSectionData
}

export interface CoSellReferralMapping {
  fieldMappings: CosellReferralDataFieldMappings
}
export interface CoSellMappingManagerData {
  // Salesforce-specific metadata fields (optional, only populated for Salesforce)
  id: string | null
  crmId: string | null
  accountId: string | null
  cloudType: string | null
  crmType: string | null
  updatedBy: string | null
  createdAt: string | null
  updatedAt: string | null
  // Section data can be either HubSpot or Salesforce structure
  sectionData:
    | CosellMappingManagerSections
    | CosellMappingManagerSectionsForSalesforce
  originalCrmSchema: any | null
  templateDetails: CosellTemplatesDetailsData[]
  referralTemplatesTotalCount: number | null
  templateData: CosellTemplateData
  crmFields: CosellHubspotFieldsDataResponse
  salesforceFields?: CosellSalesforceFieldsData
  labraFields: CosellHubspotFieldsDataResponse
  referralMapping: CosellTemplateReferralMapping
  /** Full referral templates for Salesforce stage-mapping preview, keyed by templateId */
  salesforceReferralTemplateById: Record<string, CosellTemplateDataResponse>
  hubspotReferralTemplateById: Record<string, CosellTemplateDataResponse>
}
export interface CoSellMappingManagerDataForSalesforce {
  id: string | null
  crmId: string | null
  accountId: string | null
  cloudType: string | null
  crmType: string | null
  updatedBy: string | null
  createdAt: string | null
  updatedAt: string | null
  sectionData: CosellMappingManagerSectionsForSalesforce
  originalCrmSchema: any | null
  templateDetails: CosellTemplatesDetailsData[]
  referralTemplatesTotalCount: number | null
  templateData: CosellTemplateData
  crmFields: CosellHubspotFieldsDataResponse
  labraFields: CosellHubspotFieldsDataResponse
  referralMapping: CosellTemplateReferralMapping
  salesforceReferralTemplateById: Record<string, CosellTemplateDataResponse>
}

export interface CosellSalesforceTemplatesState {
  data: CosellTemplateDataResponseCRM | null
}

const initialSalesforceTemplatesState: CosellSalesforceTemplatesState = {
  data: null,
}

export enum CosellSalesforceTemplatesActionTypes {
  SET_SALESFORCE_REFERRAL_TEMPLATES = 'SET_SALESFORCE_REFERRAL_TEMPLATES',
  CLEAR_SALESFORCE_REFERRAL_TEMPLATES = 'CLEAR_SALESFORCE_REFERRAL_TEMPLATES',
}

export type CosellSalesforceTemplatesAction =
  | {
      type: CosellSalesforceTemplatesActionTypes.SET_SALESFORCE_REFERRAL_TEMPLATES
      payload: CosellTemplateDataResponseCRM
    }
  | {
      type: CosellSalesforceTemplatesActionTypes.CLEAR_SALESFORCE_REFERRAL_TEMPLATES
    }

export const cosellSalesforceTemplatesReducer: Reducer<
  CosellSalesforceTemplatesState,
  CosellSalesforceTemplatesAction
> = (state = initialSalesforceTemplatesState, action) => {
  switch (action.type) {
    case CosellSalesforceTemplatesActionTypes.SET_SALESFORCE_REFERRAL_TEMPLATES: {
      return {
        ...state,
        data: action.payload,
      }
    }
    case CosellSalesforceTemplatesActionTypes.CLEAR_SALESFORCE_REFERRAL_TEMPLATES: {
      return initialSalesforceTemplatesState
    }
    default: {
      return state
    }
  }
}

const initialState: CoSellMappingManagerData = {
  // Salesforce metadata fields (null for HubSpot)
  id: null,
  crmId: null,
  accountId: null,
  cloudType: null,
  crmType: null,
  updatedBy: null,
  createdAt: null,
  updatedAt: null,
  sectionData: {
    [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: {
      createCompany: false,
      createContact: false,
      createSenderCompany: false,
      awsSalesRep: false,
      awsAccountManager: false,
      awsIsvSuccessManager: false,
      awsPartnerSuccessManager: false,
      partnerDevelopmentManager: false,
      wwpsPartnerDevelopmentManager: false,
      customerEndUserContactDetails: false,
      senderContact: false,
    },
    [CoSellMappingManagerSectionName.HUBSPOT_DEAL_PIPELINE]: {
      autoAcceptIncomingReferral: false,
      opportunityReferralPipeline: {
        pipelineId: '',
        pipelineDealStageId: '',
      },
      autoAcceptIncomingLead: false,
      leadReferralPipeline: {
        pipelineId: '',
        pipelineDealStageId: '',
      },
    },
    [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: {
      userDetails: [
        {
          id: `${Math.floor(Math.random() * 10000)}`,
          firstName: '',
          lastName: '',
          email: '',
        },
      ],
    },
    [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD]: {
      stage: false,
      dealOwner: false,
      nextSteps: false,
      targetCloseDate: false,
      stagesMapping: {
        prospect: [],
        qualified: [],
        technicalValidation: [],
        businessValidation: [],
        committed: [],
        launched: [],
        closedLost: [],
      },
    },
    [CoSellMappingManagerSectionName.SYNC_REFERRAL_RECORD]: {
      syncCustomHubspotFieldsForReporting: false,
      enableReferralSyncToCrm: false,
    },
  },
  originalCrmSchema: null,
  templateDetails: [] as CosellTemplatesDetailsData[],
  referralTemplatesTotalCount: null,
  templateData: {} as CosellTemplateData,
  crmFields: {} as CosellHubspotFieldsDataResponse,
  salesforceFields: {} as CosellSalesforceFieldsData,
  labraFields: {} as CosellHubspotFieldsDataResponse,
  referralMapping: {} as CosellTemplateReferralMapping,
  salesforceReferralTemplateById: {},
  hubspotReferralTemplateById: {},
}
export const initialStateForSalesforce: CoSellMappingManagerDataForSalesforce =
  {
    id: null,
    crmId: null,
    accountId: null,
    cloudType: null,
    crmType: null,
    updatedBy: null,
    createdAt: null,
    updatedAt: null,
    sectionData: {
      [CoSellMappingManagerSectionName.COMPANY_AND_CONTACT]: {
        createCompany: false,
        createContact: false,
        createSenderCompany: false,
        awsSalesRep: false,
        awsAccountManager: false,
        awsIsvSuccessManager: false,
        awsPartnerSuccessManager: false,
        partnerDevelopmentManager: false,
        wwpsPartnerDevelopmentManager: false,
        customerEndUserContactDetails: false,
        senderContact: false,
      },
      [CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS]: {
        userDetails: [
          {
            id: `${Math.floor(Math.random() * 10000)}`,
            firstName: '',
            lastName: '',
            email: '',
          },
        ],
      },
      [CoSellMappingManagerSectionName.LEAD_AND_REFERRAL_SYNC]: {
        labraReferralSync: true,
        partnerOriginatedReferralSync: true,
        awsOriginatedReferralSync: true,
        labraLeadSync: false,
        autoAcceptIncomingReferral: false,
      },
      [CoSellMappingManagerSectionName.AUTO_CREATE_ACCOUNT_AND_OPPORTUNITY]: {
        autoCreateAccount: false,
        autoCreateOpportunity: false,
        syncReferralDataToOpportunity: false,
        autoCreateMappings: {},
      },
      [CoSellMappingManagerSectionName.UPDATE_REFERRAL_RECORD_SALESFORCE]: {
        stage: false,
        targetCloseDate: false,
        stageFieldInternalName: '',
        stagesMapping: {
          prospect: [],
          qualified: [],
          technicalValidation: [],
          businessValidation: [],
          committed: [],
          launched: [],
          closedLost: [],
        },
      },
    },
    originalCrmSchema: null,
    templateDetails: [] as CosellTemplatesDetailsData[],
    referralTemplatesTotalCount: null,
    templateData: {} as CosellTemplateData,
    crmFields: {} as CosellHubspotFieldsDataResponse,
    labraFields: {} as CosellHubspotFieldsDataResponse,
    referralMapping: {} as CosellTemplateReferralMapping,
    salesforceReferralTemplateById: {},
  }

/** Deep clone of HubSpot default section data when no sync configuration exists yet. */
export const getEmptyCosellMappingManagerSections =
  (): CosellMappingManagerSections => {
    const data = JSON.parse(
      JSON.stringify(initialState.sectionData)
    ) as CosellMappingManagerSections
    const userDetails =
      data[CoSellMappingManagerSectionName.AWS_PARTNER_USER_DETAILS].userDetails
    if (userDetails[0]) {
      userDetails[0].id = `${Math.floor(Math.random() * 10000)}`
    }
    return data
  }

type actionType =
  | {
      type: CosellMappingManagerActionTypes.SET_TEMPLATE_DATA
      payload: CosellTemplateDataResponse
    }
  | {
      type: CosellMappingManagerActionTypes.CACHE_SALESFORCE_REFERRAL_TEMPLATE_FOR_STAGE_MAPPING
      payload: { templateId: string; template: CosellTemplateDataResponse }
    }
  | {
      type: CosellMappingManagerActionTypes.CACHE_HUBSPOT_REFERRAL_TEMPLATE_FOR_STAGE_MAPPING
      payload: { templateId: string; template: CosellTemplateDataResponse }
    }
  | {
      type: CosellMappingManagerActionTypes.SET_REFERRAL_MAPPING_DATA
      payload: CosellReferralDataFieldMappings
    }
  | {
      type: CosellMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO
      payload:
        | CosellTemplatesDetailsData[]
        | {
            templates: CosellTemplatesDetailsData[]
            totalCount: number
          }
    }
  | {
      type: CosellMappingManagerActionTypes.SET_HUBSPOT_FIELDS
      payload: CosellHubspotFieldsData[]
    }
  | {
      type: CosellMappingManagerActionTypes.SET_SECTION_WISE_FIELDS
      payload: CosellTemplateSectionData
    }
  | {
      type: CosellMappingManagerActionTypes.SET_SECTION_WISE_RFERRAL_MAPPING
      payload: CosellReferralMappingSectionData
    }
  | {
      type: CosellMappingManagerActionTypes.SET_CO_SELL_MAPPING_SECTION_DATA
      payload:
        | CosellMappingManagerSections
        | CosellMappingManagerSectionsForSalesforce
    }
  | {
      type: CosellMappingManagerActionTypes.SET_ORIGINAL_CRM_SCHEMA
      payload: any
    }
  | {
      type: CosellMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD
      payload: {
        section: keyof CosellTemplateSectionData
        fieldKey: keyof CosellTemplatesDataFieldMappings
        updates: Partial<CosellFieldMapping>
      }
    }
  | {
      type: CosellMappingManagerActionTypes.UPDATE_MAPPING_SECTION_FILED
      payload: {
        section: keyof CosellReferralMappingSectionData
        fieldKey: keyof CosellReferralDataFieldMappings
        updates: Partial<
          CosellSyncReferralMapping | CosellSyncNewReferralMapping
        >
      }
    }
  | {
      type: CosellMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT
      payload: boolean
    }
  | {
      type: CosellMappingManagerActionTypes.SET_AUTOMATE_REFERRAL_CREATION
      payload: boolean
    }
  | {
      type: CosellMappingManagerActionTypes.SET_AUTO_CREATE_TRIGGER_CRITERIA
      payload: AutoCreateTriggerCriteria | null
    }
  | {
      type: CosellMappingManagerActionTypes.SET_SYNC_CONFIGURATIONS
      payload: any
    }
  | {
      type: CosellMappingManagerActionTypes.SET_SALESFORCE_FIELDS
      payload: CosellSalesforceFieldsData
    }
  | {
      type: CosellMappingManagerActionTypes.SET_LABRA_FIELDS
      payload: CosellHubspotFieldsData[]
    }

export const reducer: Reducer<CoSellMappingManagerData, actionType> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case CosellMappingManagerActionTypes.SET_TEMPLATE_DATA: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.CACHE_SALESFORCE_REFERRAL_TEMPLATE_FOR_STAGE_MAPPING: {
      const { templateId, template } = action.payload
      return {
        ...state,
        salesforceReferralTemplateById: {
          ...state.salesforceReferralTemplateById,
          [templateId]: template,
        },
      }
    }
    case CosellMappingManagerActionTypes.CACHE_HUBSPOT_REFERRAL_TEMPLATE_FOR_STAGE_MAPPING: {
      const { templateId, template } = action.payload
      return {
        ...state,
        hubspotReferralTemplateById: {
          ...state.hubspotReferralTemplateById,
          [templateId]: template,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_REFERRAL_MAPPING_DATA: {
      return {
        ...state,
        referralMapping: {
          ...state.referralMapping,
          referralMappings: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO: {
      const templates = Array.isArray(action.payload)
        ? action.payload
        : action.payload.templates
      const totalCount = Array.isArray(action.payload)
        ? action.payload.length
        : action.payload.totalCount

      return {
        ...state,
        templateDetails: templates,
        referralTemplatesTotalCount: totalCount,
      }
    }
    case CosellMappingManagerActionTypes.SET_TEMPLATE_AS_DRAFT: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            isDraft: action.payload,
            isActive: !action.payload,
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_HUBSPOT_FIELDS: {
      return {
        ...state,
        crmFields: {
          fields: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_LABRA_FIELDS: {
      return {
        ...state,
        labraFields: {
          fields: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_SECTION_WISE_FIELDS: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          sectionData: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_SECTION_WISE_RFERRAL_MAPPING: {
      return {
        ...state,
        referralMapping: {
          ...state.referralMapping,
          sectionData: action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_AUTOMATE_REFERRAL_CREATION: {
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            autoReferralCreation: action.payload,
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_AUTO_CREATE_TRIGGER_CRITERIA: {
      const criteria = action.payload
      return {
        ...state,
        templateData: {
          ...state.templateData,
          templateData: {
            ...state.templateData.templateData,
            autoReferralTriggerCriteria: criteria,
            ...(criteria == null && {
              autoReferralDealtypeTrigger: '',
            }),
            ...(criteria != null &&
              criteria.object === 'Deal' &&
              criteria.field === 'dealtype' && {
                autoReferralDealtypeTrigger: criteria.value,
              }),
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_CO_SELL_MAPPING_SECTION_DATA: {
      return {
        ...state,
        sectionData: {
          ...state.sectionData,
          ...action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_SYNC_CONFIGURATIONS: {
      return {
        ...state,
        id: action.payload?.id ?? null,
        crmId: action.payload?.crmId ?? null,
        accountId: action.payload?.accountId ?? null,
        cloudType: action.payload?.cloudType ?? null,
        crmType: action.payload?.crmType ?? null,
        updatedBy: action.payload?.updatedBy ?? null,
        createdAt: action.payload?.createdAt ?? null,
        updatedAt: action.payload?.updatedAt ?? null,
      }
    }
    case CosellMappingManagerActionTypes.SET_ORIGINAL_CRM_SCHEMA: {
      return {
        ...state,
        originalCrmSchema: {
          ...state.originalCrmSchema,
          ...action.payload,
        },
      }
    }
    case CosellMappingManagerActionTypes.UPDATE_TEMPLATE_SECTION_FIELD: {
      const { section, fieldKey, updates } = action.payload
      const existingSection = (state.templateData.sectionData?.[section] ??
        {}) as Partial<CosellTemplatesDataFieldMappings>
      return {
        ...state,
        templateData: {
          ...state.templateData,
          sectionData: {
            ...state.templateData.sectionData,
            [section]: {
              ...existingSection,
              [fieldKey]: {
                ...(existingSection[fieldKey] ?? {}),
                ...updates,
              },
            },
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.UPDATE_MAPPING_SECTION_FILED: {
      const { section, fieldKey, updates } = action.payload
      return {
        ...state,
        referralMapping: {
          ...state.referralMapping,
          sectionData: {
            ...state.referralMapping.sectionData,
            [section]: {
              ...(state.referralMapping.sectionData?.[section] ?? {}),
              [fieldKey]: {
                ...(state.referralMapping.sectionData?.[section]?.[fieldKey] ??
                  {}),
                ...updates,
              },
            },
          },
        },
      }
    }
    case CosellMappingManagerActionTypes.SET_SALESFORCE_FIELDS: {
      return {
        ...state,
        salesforceFields: action.payload,
      }
    }
    default: {
      return state
    }
  }
}

export const CoSellMappingManagerDataForSalesforce: Reducer<
  CoSellMappingManagerDataForSalesforce,
  actionType
> = (state = initialStateForSalesforce, action) => {
  switch (action.type) {
    case CosellMappingManagerActionTypes.SET_SYNC_CONFIGURATIONS: {
      return {
        ...state,
        id: action.payload?.id ?? null,
        crmId: action.payload?.crmId ?? null,
        accountId: action.payload?.accountId ?? null,
        cloudType: action.payload?.cloudType ?? null,
        crmType: action.payload?.crmType ?? null,
        updatedBy: action.payload?.updatedBy ?? null,
        createdAt: action.payload?.createdAt ?? null,
        updatedAt: action.payload?.updatedAt ?? null,
      }
    }
    case CosellMappingManagerActionTypes.SET_TEMPLATE_DATA_INFO: {
      const templates = Array.isArray(action.payload)
        ? action.payload
        : action.payload.templates
      const totalCount = Array.isArray(action.payload)
        ? action.payload.length
        : action.payload.totalCount

      return {
        ...state,
        templateDetails: templates,
        referralTemplatesTotalCount: totalCount,
      }
    }
    case CosellMappingManagerActionTypes.SET_CO_SELL_MAPPING_SECTION_DATA: {
      return {
        ...state,
        sectionData: {
          ...state.sectionData,
          ...(action.payload as Partial<CosellMappingManagerSectionsForSalesforce>),
        },
      }
    }
    case CosellMappingManagerActionTypes.CACHE_SALESFORCE_REFERRAL_TEMPLATE_FOR_STAGE_MAPPING: {
      const { templateId, template } = action.payload
      return {
        ...state,
        salesforceReferralTemplateById: {
          ...state.salesforceReferralTemplateById,
          [templateId]: template,
        },
      }
    }
    default: {
      return state
    }
  }
}
